import { EProjectDiscussionStatus } from '@discussion/projectDiscussion/projectDiscussion.constants';
import { IProjectDiscussion } from '@discussion/projectDiscussion/projectDiscussion.models';
import { EProjectDiscussionFileType } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { FormFieldMethods } from '@gcl/react-core';
import { QueryKey } from '@tanstack/react-query';
import * as React from 'react';
import { useHasOverlappingCompany } from '@config/config.hooks';
import { DraftDiscussionRow } from './DraftDiscussionRow';
import { IssuedDiscussionRow } from './IssuedDiscussionRow';

interface IDiscussionRowProps {
  discussion: IProjectDiscussion;
  updateDiscussionAsDraft: (id: number) => void;
  updateDiscussionAsIssue: (id: number, issueToType: string | undefined) => void;
  updateReplyAsDraft: (id: number) => void;
  updateReplyAsIssue: (id: number, issueToType: string | undefined) => void;
  formFieldMethods: FormFieldMethods;
  onSelectDiscussion: (id: number, isSelected: boolean) => void;
  onSelectReply: (id: number, isSelected: boolean) => void;
  onDeleteDiscussion: (id: number) => void;
  isLoadingDeleteDiscussion: boolean;
  isErrorDeleteDiscussion: boolean;
  isSuccessDeleteDiscussion: boolean;

  isLoadingUpdateAsDraft: boolean;
  isErrorUpdateAsDraft: boolean;
  isSuccessUpdateAsDraft: boolean;

  isLoadingUpdateAsIssue: boolean;
  isErrorUpdateAsIssue: boolean;
  isSuccessUpdateAsIssue: boolean;

  fileFormType: EProjectFileType;
  discussionFileType: EProjectDiscussionFileType;
  issueToTypes?: string[];
  useFetchFilesQueryKey?: QueryKey;
  canComment: boolean;
  canResponse: (discussion: IProjectDiscussion) => boolean;
  isFileUploading: boolean;
  onFileUploading: (uuid: string, isUploading: boolean) => void;
  isAnyActionLoading: boolean;
  canViewDraftComment: boolean;
}

export const DiscussionRow = ({
  discussion,
  updateDiscussionAsDraft,
  updateDiscussionAsIssue,
  updateReplyAsDraft,
  updateReplyAsIssue,
  formFieldMethods,
  onSelectDiscussion,
  onSelectReply,
  onDeleteDiscussion,
  isLoadingDeleteDiscussion,
  isErrorDeleteDiscussion,
  isSuccessDeleteDiscussion,
  isLoadingUpdateAsDraft,
  isErrorUpdateAsDraft,
  isSuccessUpdateAsDraft,
  isLoadingUpdateAsIssue,
  isErrorUpdateAsIssue,
  isSuccessUpdateAsIssue,
  fileFormType,
  discussionFileType,
  issueToTypes = [],
  useFetchFilesQueryKey,
  canComment,
  canResponse,
  isFileUploading,
  onFileUploading,
  isAnyActionLoading,
  canViewDraftComment,
}: IDiscussionRowProps) => {
  const hasOverlappingCompany = useHasOverlappingCompany(discussion.repliedByBelongsToCompanies?.map((s) => s.id));
  const isDraftResponseExist = !!discussion.repliedBy;
  const canViewDraftResponse = discussion.status === EProjectDiscussionStatus.DRAFT_RESPONSE && hasOverlappingCompany;

  switch (discussion.status) {
    case EProjectDiscussionStatus.DRAFT_COMMENT:
      return canViewDraftComment ? (
        <DraftDiscussionRow
          isAnyActionLoading={isAnyActionLoading}
          isFileUploading={isFileUploading}
          onFileUploading={onFileUploading}
          canComment={canComment}
          canResponse={canResponse}
          useFetchFilesQueryKey={useFetchFilesQueryKey}
          issueToTypes={issueToTypes}
          discussion={discussion}
          onDeleteDiscussion={onDeleteDiscussion}
          onSelected={onSelectDiscussion}
          formFieldMethods={formFieldMethods}
          isLoadingDeleteDiscussion={isLoadingDeleteDiscussion}
          isErrorDeleteDiscussion={isErrorDeleteDiscussion}
          isSuccessDeleteDiscussion={isSuccessDeleteDiscussion}
          updateAsDraft={updateDiscussionAsDraft}
          updateAsIssue={updateDiscussionAsIssue}
          isLoadingUpdateAsDraft={isLoadingUpdateAsDraft}
          isErrorUpdateAsDraft={isErrorUpdateAsDraft}
          isSuccessUpdateAsDraft={isSuccessUpdateAsDraft}
          isLoadingUpdateAsIssue={isLoadingUpdateAsIssue}
          isErrorUpdateAsIssue={isErrorUpdateAsIssue}
          isSuccessUpdateAsIssue={isSuccessUpdateAsIssue}
          fileFormType={fileFormType}
          discussionFileType={discussionFileType}
        />
      ) : null;
    case EProjectDiscussionStatus.COMMENTED:
    case EProjectDiscussionStatus.DRAFT_RESPONSE:
      return (
        <IssuedDiscussionRow
          discussion={discussion}
          fileFormType={fileFormType}
          discussionFileType={discussionFileType}
          bottomComponent={
            (canResponse(discussion) && !isDraftResponseExist) || (isDraftResponseExist && canViewDraftResponse) ? (
              <DraftDiscussionRow
                isAnyActionLoading={isAnyActionLoading}
                isFileUploading={isFileUploading}
                onFileUploading={onFileUploading}
                canComment={canComment}
                canResponse={canResponse}
                useFetchFilesQueryKey={useFetchFilesQueryKey}
                isReply
                discussion={discussion}
                onSelected={onSelectReply}
                formFieldMethods={formFieldMethods}
                updateAsDraft={updateReplyAsDraft}
                updateAsIssue={updateReplyAsIssue}
                isLoadingUpdateAsDraft={isLoadingUpdateAsDraft}
                isErrorUpdateAsDraft={isErrorUpdateAsDraft}
                isSuccessUpdateAsDraft={isSuccessUpdateAsDraft}
                isLoadingUpdateAsIssue={isLoadingUpdateAsIssue}
                isErrorUpdateAsIssue={isErrorUpdateAsIssue}
                isSuccessUpdateAsIssue={isSuccessUpdateAsIssue}
                fileFormType={fileFormType}
                discussionFileType={discussionFileType}
              />
            ) : undefined
          }
        />
      );
    case EProjectDiscussionStatus.RESPONSED:
    default:
      return (
        <IssuedDiscussionRow
          discussion={discussion}
          fileFormType={fileFormType}
          discussionFileType={discussionFileType}
          bottomComponent={
            <IssuedDiscussionRow
              discussion={discussion}
              isReply
              fileFormType={fileFormType}
              discussionFileType={discussionFileType}
            />
          }
        />
      );
  }
};
